import type * as React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { Paperclip } from "lucide-react";
import { cn } from "../../../lib/cn";
import { CitationHighlighter } from "../CitationHighlighter";
import type { CitationAnchor } from "../types";

export interface EmailAttachment {
  id: string;
  filename: string;
  sizeBytes?: number;
  mimeType?: string;
}

export interface EmailPayload {
  from: string;
  to: string[];
  cc?: string[];
  subject: string;
  date: string;
  /** Body in markdown (we sanitize) — render upstream from HTML if needed. */
  bodyMarkdown: string;
  attachments?: EmailAttachment[];
}

export interface EmailRenderProps {
  payload: EmailPayload;
  anchor?: CitationAnchor;
}

/** Same hardened schema used by `MessageBody` — no `dangerouslySetInnerHTML`. */
const sanitizeSchema: typeof defaultSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a ?? []), ["target"], ["rel"]],
  },
};

function formatBytes(b?: number): string {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * EmailRender — header (from/to/cc/subject/date) + sanitised markdown body
 * + attachments list. Highlights the cited paragraph via the universal
 * `<CitationHighlighter>` when `anchor.text` matches a body chunk.
 *
 * We deliberately reuse the same markdown + rehype-sanitize pipeline as
 * `MessageBody` rather than trusting raw HTML from the BE — XSS-proof by
 * construction.
 */
export const EmailRender: React.FC<EmailRenderProps> = ({ payload, anchor }) => {
  const wantHighlight = Boolean(anchor?.text);

  // For the scaffold we either highlight the whole body (anchor present)
  // or render plainly. Production swap: split body on `anchor.text` and
  // wrap only the matching span. The viewer contract stays identical.
  const Body = (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
    >
      {payload.bodyMarkdown}
    </ReactMarkdown>
  );

  return (
    <div className="flex flex-col gap-3 text-sm">
      <header className="rounded-md border border-hairline bg-surface-1/60 px-3 py-2">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-display font-semibold text-base text-ink-1 tracking-tight">
            {payload.subject}
          </div>
          <time className="font-mono text-[11px] text-ink-3 tabular-nums">
            {new Date(payload.date).toLocaleString()}
          </time>
        </div>
        <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-ink-2 text-xs">
          <dt className="text-ink-3">Da</dt>
          <dd className="truncate font-mono">{payload.from}</dd>
          <dt className="text-ink-3">A</dt>
          <dd className="truncate font-mono">{payload.to.join(", ")}</dd>
          {payload.cc?.length ? (
            <>
              <dt className="text-ink-3">Cc</dt>
              <dd className="truncate font-mono">{payload.cc.join(", ")}</dd>
            </>
          ) : null}
        </dl>
      </header>

      <CitationHighlighter
        active={wantHighlight}
        className={cn("prose prose-sm max-w-none text-ink-1", !wantHighlight && "p-0")}
      >
        {Body}
      </CitationHighlighter>

      {payload.attachments?.length ? (
        <section>
          <div className="mb-1 font-medium text-[10px] text-ink-3 uppercase tracking-wide">
            Allegati
          </div>
          <ul className="flex flex-col gap-1">
            {payload.attachments.map((a) => (
              <li
                key={a.id}
                className={cn(
                  "flex items-center gap-2 rounded-md border border-hairline",
                  "bg-canvas px-2 py-1.5 text-ink-2 text-xs",
                )}
              >
                <Paperclip className="h-3.5 w-3.5 text-ando-orange" />
                <span className="truncate">{a.filename}</span>
                {a.sizeBytes ? (
                  <span className="ml-auto font-mono text-[10px] text-ink-3 tabular-nums">
                    {formatBytes(a.sizeBytes)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
};
EmailRender.displayName = "EmailRender";
